import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import HudAccordian from 'ff/debrief/components/hud/HudAccordian';
import { CurrentPoint } from 'ff/debrief/models';
import {
    AirspeedSection,
    AltitudeSection,
    HeadingSection,
    HorizonWidget,
} from 'ff/debrief/components/hud/widgets';
import { HudRenderer } from 'ff/debrief/components/hud/index';
import { useChrono, useCurrentAircraft } from 'ff/debrief/hooks';
import HudPanel from 'ff/debrief/layouts/HudPanel';

const HudContainer = styled.div`
    pointer-events: none;
    background-color: transparent;
    width: 260px;
    height: 260px;
`;

// FUTURE: Dynamically determine available properties (e.g., G1000 will provide more data than a basic tracklog)
// Currently hardcoded to all common properties (omits gLoad and cabinAltitudeFeet which are only available from
// specific sources)
const AVAILABLE_POINT_PROPS: (keyof CurrentPoint)[] = [
    'timestamp',
    'latitude',
    'longitude',
    'groundSpeed',
    'altitudeFeet',
    'bankDeg',
    'pitchDeg',
    'trueAirSpeed',
    'magneticCourse',
    'magneticHeading',
    'windDirection',
    'windVelocity',
    'verticalSpeed',
    'turnRate',
    'magneticVariance',
    'indicatedAirSpeed',
    'crosswind',
    'headwind',
    'aboveGroundLevel',
    'bearing',
];

const Hud = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { currentPoint } = useChrono();
    const currentPointRef = useRef<CurrentPoint>(null);
    const { speedUnit } = useCurrentAircraft();
    const [hudRenderer, setHudRenderer] = useState<HudRenderer>(null);
    const [isHudExpanded, setIsHudExpanded] = useState(true);

    // Keep ref in sync with latest currentPoint
    currentPointRef.current = currentPoint;

    const setContainerRef = useCallback((node: HTMLDivElement) => {
        containerRef.current = node;
    }, []);

    useEffect(() => {
        const renderer = new HudRenderer(
            containerRef.current,
            AVAILABLE_POINT_PROPS,
            {
                fontFamily: 'monospace',
                baseFontSize: 10,
                primaryLineColor: 0x000000,
            },
            undefined, // pixiAppFactory
            speedUnit ?? 'kts'
        );
        console.log('Initializing HUD renderer');
        renderer
            .init({
                backgroundColor: 0x555555,
                antialias: true,
            })
            .then(() => console.log('initialized pixiApp'))

            // FUTURE ENHANCEMENT: Dynamically add/config widgets based on the fields available `currentPoint`
            .then(() => {
                const horizonWidget = new HorizonWidget();
                return renderer.addWidget(horizonWidget, {
                    x: 0,
                    y: 0,
                    width: 1,
                    height: 1,
                });
            })

            .then(() => {
                const headingSection = new HeadingSection();
                return renderer.addWidget(headingSection, {
                    x: 0.25,
                    y: 0,
                    width: 0.5,
                    height: 0.1,
                });
            })

            .then(() => {
                const airspeedSection = new AirspeedSection();
                return renderer.addWidget(airspeedSection, {
                    x: 0.01,
                    y: 0,
                    width: 0.24,
                    height: 1,
                });
            })

            .then(() => {
                const altitudeGroup = new AltitudeSection();
                return renderer.addWidget(altitudeGroup, {
                    x: 0.75,
                    y: 0.1,
                    width: 0.24,
                    height: 0.8,
                });
            })

            .then(() => setHudRenderer(renderer))
            .then(() => {
                if (currentPoint) {
                    renderer.update(currentPoint);
                }
            })
            .catch(console.error);
        return () => {
            renderer.destroy().catch(console.error);
        };
    }, [containerRef]);

    useEffect(() => {
        if (isHudExpanded && hudRenderer && currentPoint) {
            hudRenderer.update(currentPoint);
        }
    }, [currentPoint, isHudExpanded, hudRenderer]);

    useEffect(() => {
        if (isHudExpanded && hudRenderer && speedUnit) {
            hudRenderer.setSpeedUnit(speedUnit);

            // Trigger a re-render with the current point to show updated values on speedUnit change
            if (currentPointRef.current) {
                hudRenderer.update(currentPointRef.current);
            }
        }
    }, [speedUnit, hudRenderer, isHudExpanded]);

    return (
        <HudPanel>
            <HudAccordian onExpandedChange={setIsHudExpanded}>
                <HudContainer ref={setContainerRef} />
            </HudAccordian>
        </HudPanel>
    );
};

export default Hud;
